import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OverlayModule } from '@angular/cdk/overlay';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';

import * as AuthActions from '../../../auth/store/auth.actions';
import * as AuthSelectors from '../../../auth/store/auth.selectors';
import { ThemeService, Theme } from '../../../services/theme.service';
import { RoutePaths } from '../../../shared/api/types/enums';
import { TopbarMenuService } from '../topbar-menu.service';
import { ChromeReadyService } from '../../chrome-ready.service';

/**
 * User menu (NBO-2331): name + email header — no invented role subtitles — a
 * Theme Light/Dark segment, Settings, Help Center, Developer Docs, Sign out.
 *
 * The theme segment is **dark mode's first reachable home**: ThemeService has
 * shipped for a long time with zero consumers, which is the NBO-2268
 * unreachable-dark finding. No language row here — the language switcher is
 * context and lives in the bar (design canon §2).
 */
@Component({
  selector: 'app-topbar-user-menu',
  standalone: true,
  imports: [OverlayModule, RouterLink, TranslateModule, MatIconModule],
  templateUrl: './topbar-user-menu.component.html',
  styleUrls: ['./topbar-user-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopbarUserMenuComponent {
  // Shared across the five clusters so only one popover is open and switching
  // between triggers takes one click (see TopbarMenuService).
  readonly open = this.menu.openFor('user');
  readonly routePaths = RoutePaths;

  /** Shared with the rest of the identity cluster — one reveal (NBO-2331 r4). */
  readonly ready = inject(ChromeReadyService).identityReady;

  readonly helpCenterUrl = 'https://help.propeller-commerce.com/';
  readonly developerDocsUrl = 'https://docs.propeller-commerce.com/';

  private readonly adminUser = toSignal(this.store.select(AuthSelectors.selectAuthAdminUser), {
    initialValue: null
  });

  /** Off on production until the redesign is complete — see THEME_SWITCH_ENABLED. */
  readonly themeSwitchEnabled = this.themeService.switchEnabled;

  readonly theme = toSignal(this.themeService.currentTheme$, { initialValue: this.themeService.getCurrentTheme() });

  readonly name = computed(() => {
    const user = this.adminUser();
    if (!user) return '';
    const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return full || user.email || '';
  });

  readonly email = computed(() => this.adminUser()?.email ?? '');

  /**
   * Empty — never a '?' — while the profile is still in flight. The old
   * fallback fabricated a value: on a cold load the bar painted a "?" avatar
   * for ~4s (measured), which reads as a real, if unknown, user rather than as
   * "not loaded yet". A value the data does not support must not be invented;
   * the pill renders as an empty shape until the initials are known.
   */
  readonly initials = computed(() => {
    const user = this.adminUser();
    if (!user) return '';
    const first = user.firstName?.charAt(0) ?? '';
    const last = user.lastName?.charAt(0) ?? '';
    const combined = (first + last).trim();
    return (combined || this.email().charAt(0) || '').toUpperCase();
  });

  constructor(
    private menu: TopbarMenuService,
    private store: Store,
    private themeService: ThemeService
  ) {}

  toggle(): void {
    this.menu.toggle('user');
  }

  close(): void {
    this.menu.close();
  }

  onBackdropClick(event: MouseEvent): void {
    this.menu.handleBackdropClick(event);
  }

  onDetach(): void {
    this.menu.closeIf('user');
  }

  setTheme(theme: Theme): void {
    this.themeService.setTheme(theme);
  }

  logout(): void {
    this.close();
    this.store.dispatch(AuthActions.logoutStart());
  }
}
